import type { AuthUser } from '@/types/user'

export const useAuth = () => {
  const {
    public: { API_URL }
  } = useRuntimeConfig()

  const authStore = useAuthStore()
  const { token, setToken, setExpires } = useApolloToken()
  const { fetchFeatureFlags } = useFeatureFlagsStore()
  const { refreshConvertVariations } = useConvert()

  const login = async (credentials: { username: string; password: string }) => {
    const response = await $fetch<{ token: string; token_expires_at: string }>(`${API_URL}/login_check`, {
      method: 'POST',
      body: credentials,
      credentials: 'include' // To accept (and persist) cookies returned form the server
    })

    authStore.setAuthToken(response.token, response.token_expires_at)

    setToken(response.token)
    setExpires(response.token_expires_at)

    // Refresh convert variations and feature flags after successful login
    await Promise.all([refreshConvertVariations(), fetchFeatureFlags()])

    return response
  }

  const passwordReset = (email: string) => {
    return $fetch(`${API_URL}/v2/password-reset`, {
      method: 'POST',
      body: { email }
    })
  }

  const confirmPasswordReset = (token: string, password: string, passwordRepeat: string, _hash?: string) => {
    const url = `${API_URL}/v2/password-reset/${token}${_hash ? `?_hash=${encodeURIComponent(_hash)}` : ''}`

    return $fetch(url, {
      method: 'PUT',
      body: {
        password,
        passwordRepeat
      }
    })
  }

  const fetchUser = async (): Promise<AuthUser> => {
    const query = `
      query GetAuthUser {
        meAuthUser {
          _id
          id
          fullName
          firstName
          lastName
          email
          createdAt
          updatedAt
          dateOfBirth
          avatar
          user {
            _id
            id
            firstName
            lastName
            email
            phoneNumber
            canMarkToRetain
            isMarkedToRetain
            nextPaymentDate
            nextDeliveryDate
            lastPaymentDate
            lastDeliveryDate
            hasActiveMainMembership
            externalIdentifierTypes
            paymentContract {
              status
              active
              __typename
            }
            userVariants {
              edges {
                node {
                  variant
                  value
                  createdAt
                }
              }
            }
            orderBundles(order: [{createdAt: "DESC"}]) {
              edges {
                node {
                  token
                  payment {
                    status
                    __typename
                  }
                  orders {
                    edges {
                      node {
                        returnWindowOpensAt
                        delayedDeliveryDate
                        product {
                          design {
                            name
                            __typename
                          }
                          __typename
                        }
                        shipment {
                          deliveryDate
                          status
                          __typename
                        }
                        __typename
                      }
                      __typename
                    }
                    __typename
                  }
                  __typename
                }
                __typename
              }
              __typename
            }
            country {
              _id
              id
              name
              canCancelSubscriptionAfterDays
              expectedDeliveryDateDailyOrderAfterDays
              canDoReturnRequestAfterDay
              __typename
            }
            addresses {
              edges {
                node {
                  _id
                  id
                  street
                  fullStreet
                  streetType
                  houseNumber
                  annex
                  building
                  city
                  postalCode
                  country {
                    _id
                    id
                    name
                    hostNamePrefix
                    languages {
                      edges {
                        node {
                          id
                          iso
                          __typename
                        }
                        __typename
                      }
                      __typename
                    }
                    __typename
                  }
                  __typename
                }
                __typename
              }
              __typename
            }
            billingAddress {
              _id
              id
              street
              fullStreet
              streetType
              houseNumber
              annex
              building
              city
              postalCode
              country {
                _id
                id
                name
                hostNamePrefix
                __typename
              }
              __typename
            }
            __typename
          }
          __typename
        }
      }
    `

    const response = await $fetch<{ data: { meAuthUser: AuthUser } }>(`${API_URL}/graphql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token.value}`
      },
      body: {
        operationName: 'GetAuthUser',
        variables: {},
        query
      }
    })

    const user = response.data.meAuthUser

    authStore.setAuthUser(user)

    return user
  }

  const user = computed<AuthUser | null>(() => authStore.user)
  const isAuthenticated = computed(() => authStore.isAuthenticated)

  return {
    login,
    passwordReset,
    confirmPasswordReset,
    fetchUser,
    refreshConvertVariations,
    user,
    isAuthenticated
  }
}
